export const buildVariationData = (items, format) =>
  items.map((item, i) => ({
    value: format(item.value),
    variation: i > 0 ? item.variation : undefined,
  }));

/**
 * Factory that creates data-building helpers for key-number rows.
 *
 * Both `buildOtherRows` and `buildTonnageRows` follow the same pattern:
 *   1. Build "previous" data cells from `previous` object
 *   2. Build "suggest" data cells from `years` array
 *   3. Concatenate both into a single `data` array per row
 *   4. Optionally build children rows per culture
 *
 * @param {Object} options
 * @param {Array}  options.years
 * @param {Object} [options.previous]
 * @param {number} [options.previousCount]
 * @param {Array}  [options.activeCultures]
 */
export const createRowBuilder = ({ years, previous, previousCount = 1, activeCultures = [] }) => {
  /**
   * Build data cells from the `previous` object.
   * @param {Function} getValue  - (previous, index) => rawValue
   * @param {Function} format
   */
  const buildPrev = (getValue, format) => {
    if (!previous) return [];
    return Array.from({ length: previousCount }, (_, i) => ({
      value: format(getValue(previous, i)),
    }));
  };

  /**
   * Build data cells from suggest years.
   * @param {Function} getValue  - (year) => rawValue
   * @param {Function} format
   */
  const buildYears = (getValue, format) => years.map((year) => ({ value: format(getValue(year)) }));

  /**
   * Combine previous + suggest into a single data array.
   *
   * @param {Object}   options
   * @param {Function} options.fromYear  - (year) => rawValue
   * @param {Function} options.format
   * @param {Function} [options.fromPrev]  - (previous, index) => rawValue
   */
  const buildData = ({ fromYear, format, fromPrev }) => [
    ...buildPrev(fromPrev ?? (() => null), format),
    ...buildYears(fromYear, format),
  ];

  /**
   * Build a simple flat row (no children).
   *
   * @param {Object}   options
   * @param {string}   options.id
   * @param {Function} options.fromYear
   * @param {Function} options.format
   * @param {Function} [options.fromPrev]
   * @param {Object}   [options.extra]    - Any extra row props (invertVariation, etc.)
   */
  const buildRow = ({ id, fromYear, format, fromPrev, extra = {} }) => ({
    id,
    ...extra,
    data: buildData({ fromYear, format, fromPrev }),
  });

  /**
   * Build children rows, one per active culture.
   *
   * @param {Object}   options
   * @param {Function} options.fromYear  - (year, culture) => rawValue
   * @param {Function} options.format
   * @param {Function} [options.fromPrev]  - (previous, culture) => rawValue
   */
  const buildCultureChildren = ({ fromYear, format, fromPrev }) =>
    activeCultures.map(({ culture }) => ({
      id: culture.name,
      data: buildData({
        fromYear: (year) => fromYear(year, culture),
        format,
        fromPrev: fromPrev ? (prev) => fromPrev(prev, culture) : undefined,
      }),
    }));

  return { buildPrev, buildYears, buildData, buildRow, buildCultureChildren };
};
